import {defineComponent,h,onBeforeUnmount,ref,watch,type PropType} from 'vue';
import type {createEvidenceClient,MaterialReviewMessage,MaterialReviewResult} from '@knowyet/sdk/evidence';

const receiptLabels:Record<string,string>={recorded:'决定已记录，尚未生效',applied:'修订已生效',unresolved:'保留疑问，仍未确认',conflict:'事项已变化，未应用',needs_clarification:'问题已记录，等待进一步说明'};
export function reviewReceipt(result:MaterialReviewResult):string[] {
  const lines=result.decisions.map(d=>receiptLabels[d.status]);
  if(result.application_errors.length)lines.push('部分修订尚未完成，可继续对话或从材料进度中恢复。');
  if(result.evidence_changed)lines.push('材料已发布新版本。后续问答使用新版本，历史答案仍保留原依据。');
  return lines;
}

export const MaterialConversation=defineComponent({
  props:{workspace:{type:String,required:true},client:{type:Object as PropType<ReturnType<typeof createEvidenceClient>>,required:true},
    reviewIds:{type:Array as PropType<string[]>,default:()=>[]},busy:Boolean},
  emits:['updated','busy','clearFocus'],
  setup(props,{emit}) {
    const conversation=ref(''),items=ref<MaterialReviewMessage[]>([]),draft=ref(''),error=ref(''),sending=ref(false),loading=ref(false),hasMore=ref(false);
    let generation=0,timer:ReturnType<typeof setTimeout>|undefined,destroyed=false,initialized=false;
    let retry:{message_id:string;text:string;review_ids:string[];parent_ref?:MaterialReviewResult['turn_ref']}|undefined;
    const uuid=()=>crypto.randomUUID();
    const storage=()=>`ehf.material-thread.${props.workspace}`;
    const active=()=>items.value.some(m=>m.status==='submitted'||m.status==='queued');
    async function load(older=false) {
      if(!conversation.value||destroyed)return;
      const seq=generation,workspace=props.workspace,thread=conversation.value;
      try {
        const data=await props.client.materialConversations.messages(workspace,thread,older?{before:items.value[0]?.sequence}:{});
        if(seq!==generation||destroyed)return;
        const old=new Map(items.value.map(m=>[m.message_id,m]));
        if(older)items.value=[...data.items,...items.value];
        else {const earliest=data.items[0]?.sequence??Infinity;items.value=[...items.value.filter(m=>m.sequence<earliest),...data.items];}
        if(older||items.value.length===data.items.length)hasMore.value=data.has_more;
        emit('busy',items.value.some(m=>m.status==='submitted'));
        if(initialized&&data.items.some(m=>m.status==='completed'&&old.get(m.message_id)?.status!=='completed'))emit('updated');
        initialized=true;
      }catch(e){if(seq===generation)error.value=(e as Error).message;}
    }
    function poll() {
      clearTimeout(timer);if(destroyed)return;
      timer=setTimeout(async()=>{await load();poll();},active()?1200:5000);
    }
    function newThread() {
      generation++;clearTimeout(timer);initialized=false;
      conversation.value=uuid();localStorage.setItem(storage(),conversation.value);
      items.value=[];hasMore.value=false;retry=undefined;draft.value='';error.value='';
      emit('busy',false);emit('clearFocus');poll();
    }
    watch(()=>props.workspace,async()=>{
      const seq=++generation;initialized=false;clearTimeout(timer);conversation.value='';items.value=[];draft.value='';error.value='';retry=undefined;hasMore.value=false;sending.value=false;emit('busy',false);loading.value=true;
      try {
        const previous=await props.client.materialConversations.list(props.workspace);
        if(seq!==generation)return;
        const stored=localStorage.getItem(storage());
        conversation.value=(stored&&previous.some(c=>c.id===stored)?stored:previous[0]?.id)??uuid();
        localStorage.setItem(storage(),conversation.value);await load();
      }catch(e){if(seq===generation)error.value=(e as Error).message;}
      finally{if(seq===generation){loading.value=false;poll();}}
    },{immediate:true});
    onBeforeUnmount(()=>{destroyed=true;generation++;clearTimeout(timer);emit('busy',false);});
    async function send() {
      if(!draft.value.trim()||sending.value||!conversation.value)return;
      const seq=generation,workspace=props.workspace,thread=conversation.value;
      // An uncertain network response retries the same immutable request ID.
      if(!retry||retry.text!==draft.value||retry.review_ids.join()!==props.reviewIds.join()) {
        const parent=active()?null:items.value.at(-1)?.result?.turn_ref;
        retry={message_id:uuid(),text:draft.value,review_ids:[...props.reviewIds],...(parent?{parent_ref:parent}:{})};
      }
      sending.value=true;error.value='';
      try{
        const message=await props.client.materialConversations.send(workspace,thread,retry);
        if(seq!==generation)return;
        if(!items.value.some(m=>m.message_id===message.message_id))items.value=[...items.value,message];
        draft.value='';retry=undefined;poll();
      }catch(e){if(seq===generation)error.value=(e as Error).message;}
      finally{if(seq===generation)sending.value=false;}
    }
    async function cancel(message:MaterialReviewMessage) {
      try{
        if(message.status==='queued')await props.client.materialConversations.cancel(props.workspace,conversation.value,message.message_id);
        else await props.client.cancel(message.task_id);
        await load();
      }catch(e){error.value=(e as Error).message;}
    }
    return ()=>h('section',{class:'material-conversation','data-testid':'material-conversation'},[
      h('div',{class:'section-title'},[h('h3','与材料助手对话'),h('button',{disabled:loading.value||sending.value||active(),onClick:newThread},'新建材料对话')]),
      h('p',{class:'muted'},'可以直接说明哪里有误、补充含义，或先询问如何处理。明确修正会自动应用，原件和历史版本保留。'),
      props.reviewIds.length?h('p',[`正在讨论 ${props.reviewIds.length} 个事项。`,h('button',{onClick:()=>emit('clearFocus')},'取消事项限定')]):null,
      hasMore.value?h('button',{onClick:()=>void load(true)},'加载更早的对话'):null,
      ...items.value.map(m=>h('article',{class:'material-turn','data-message-id':m.message_id},[
        h('p',{class:'material-user'},m.text),
        m.result?h('div',{class:'material-reply'},[h('p',m.result.reply),...reviewReceipt(m.result).map(line=>h('p',{class:'muted','data-testid':'material-receipt'},line))]):
          h('p',{role:'status'},({queued:'消息已保存，等待工作区空闲后处理。',submitted:'正在理解说明并处理材料…',failed:'本次处理未完成，已保存的步骤可从材料进度中恢复。',cancelled:'本次处理已停止，保存的记录仍保留。'} as Record<string,string>)[m.status]),
        m.error?h('p',{role:'alert'},m.error):null,
        ['queued','submitted'].includes(m.status)?h('button',{onClick:()=>void cancel(m)},m.status==='queued'?'撤回待处理消息':'停止本次处理'):null,
      ])),
      error.value?h('p',{role:'alert'},error.value):null,
      h('label',['说明或提问',h('textarea',{'aria-label':'材料对话消息',value:draft.value,maxLength:4000,placeholder:'例如：这里是5月8日，请修正；或：这项需要我核对什么？',onInput:(e:Event)=>draft.value=(e.target as HTMLTextAreaElement).value})]),
      h('button',{'data-testid':'send-material-message',disabled:sending.value||loading.value||!draft.value.trim(),onClick:()=>void send()},sending.value?'正在保存…':props.busy?'发送，等待处理':'发送'),
    ]);
  }
});
